import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import { useChat } from "../../../context/chatContext";
import { useCases } from "../../../utils/constants";
import ChatHistory from "../chatHistory";
import Messages from "../messages";
import UseCaseCards from "../useCaseCards";
import ChatFooter from "./chatFooter";
import ChatHeader from "./chatHeader";
import { handleOptionSelect } from "../../../utils/functions";
import TopicSuggestionCards from "../topicSuggestonCards";

const ChatBody = ({ useCase }: { useCase?: number }) => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const [initialQuestion, setInitialQuestion] = useState<string>("");
	const {
		selectedUseCase,
		selectedSubCase,
		chatId,
		renderFromHistory,
		conversation,
		showCards,
		setSelectedSubCase,
		setRenderFromHistory,
		setShowCards,
		startChat,
	} = useChat();

	const [showMessage, setShowMessage] = useState<boolean>(false);
	const [isSelecting, setIsSelecting] = useState(false);
	const [disabled, setDisabled] = useState<boolean>(false);
	const [errorCount, setErrorCount] = useState(0);
	const [messages, setMessages] = useState<
		{
			type: string;
			text: string;
			time: string;
			icon: boolean;
			typewriter?: boolean;
			partial?: boolean;
		}[]
	>([]);

	const [eventSource, setEventSource] =
		useState<ReadableStreamDefaultReader | null>(null);

	const addMessage = useCallback((text: string, type: string) => {
		setMessages((prevMessages) => [
			...prevMessages,
			{
				type: type,
				text,
				time: new Date().toLocaleTimeString(),
				icon: false,
			},
		]);
	}, []);

	const addToLastMessage = useCallback((text: string, type: string) => {
		setMessages((prevMessages) => {
			const updatedMessages = [...prevMessages];
			const lastMessage = updatedMessages[updatedMessages.length - 1];
			// Create a new message object
			const newMessage = {
				...lastMessage,
				text: lastMessage.text + text,
				time: new Date().toLocaleTimeString(),
				typewriter: true,
				type,
			};
			// Replace the last message with the new one
			updatedMessages[updatedMessages.length - 1] = newMessage;
			return updatedMessages;
		});
	}, []);

	const handleSelect = async (index: number, question?: string) => {
		await handleOptionSelect(useCases, index, navigate, isSelecting, setIsSelecting);
		if(question){
			setInitialQuestion(question);
			console.log(question);
		}
	};

	const handleNoChatId = () => {
		setShowCards(false);
		setShowMessage(true);
		setTimeout(() => {
			addMessage("Select one of the following Areas of interest:", "left");
		}, 500);
		setDisabled(false);
	};

	const createPayload = (label: string, chat_id: string, subCase?: string) => {
		return {
			question: label,
			filter: {},
			conversation_id: chat_id,
			label: `use_case_${selectedUseCase}${
				selectedUseCase === 3 && subCase ? `.${subCase}` : ""
			}`,
		};
	};

	const fetchChatResponse = async (token: string | null, payload: any) => {
		return await fetch(
			process.env.REACT_APP_SERVER_URL + "/chat/chat" ||
				"https://chat.1ciso.io/backend",
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(payload),
			}
		);
	};

	const handleAuthError = () => {
		logout();
		navigate("/");
	};

	const handleResponseError = (customMessage?: string) => {
		addMessage(
		  customMessage || "An error occurred while processing your request. Please try again.",
		  "error"
		);
		setErrorCount(prev => prev + 1);
		setDisabled(false);
	};

	const handleApiRequest = async (token: string | null, payload: any) => {
		if (!token) {
		  navigate("/");
		  return null;
		}
	  
		try {
		  const response = await fetchChatResponse(token, payload);
		  
		  if (response.status === 401 || response.status === 403) {
			handleAuthError();
			return null;
		  }
		  
		  if (!response.ok) {
			handleResponseError();
			return null;
		  }
		  
		  return response;
		} catch (error: any) {
		  handleRequestError(error);
		  return null;
		}
	};

	const processStreamResponse = async (response: Response) => {
		if (response.body) {
			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = "";
			let text = "";
			let done;
			while (!done) {
				const result = await reader.read();
				done = result.done;
				if (done) break;
				
				buffer += decoder.decode(result.value, { stream: true });
				// Split buffer by newline characters
				const lines = buffer.split("\n");
				buffer = lines.pop() ?? "";
				
				text = processResponseLines(lines, text);
			}
			setMessages([
				{
					type: "left",
					text,
					time: new Date().toLocaleTimeString(),
					icon: false,
				},
			]);
			setDisabled(false);
		}
	};

	const processResponseLines = (lines: string[], text: string) => {
		lines.forEach((line) => {
			if (line.startsWith("data: ")) {
				const dataString = line.substring(6); // Remove 'data: ' prefix
				try {
					const data = JSON.parse(dataString);
					if (data.message) {
						text += data.message + "\n\t";
					}
				} catch (e) {
					console.error("Error parsing JSON:", e);
					setErrorCount(errorCount + 1);
					setDisabled(false);
				}
			}
		});
		return text;
	};

	const handleRequestError = (error: any) => {
		setErrorCount(errorCount + 1);
		console.error("Error sending request or handling response:", error);
		addMessage(
			"An error occurred while processing your request. Please try again.",
			"error"
		);
		setDisabled(false);

		if (error?.response?.status === 401) {
			console.log("use token expired");
			logout();
			navigate("/");
		}
	};

	const handleRadioChange = async (
		label: string,
		chat_id: string,
		type: string,
		subCase?: string
	) => {
		const payload = createPayload(label, chat_id, subCase);
		if (!chat_id) {
			handleNoChatId();
			return;
		}

		try {
			const token = localStorage.getItem("token");
			if (!token) {
				navigate("/");
			}

			const response = await handleApiRequest(token, payload);
			
			if (response) {
				await processStreamResponse(response);
			}
		} catch (error: any) {
			handleRequestError(error);
			// implement more specific error handling logic here
			if (error instanceof TypeError) {
				// Handle network errors (e.g., network down, CORS issues)
				console.error("Network error:", error);
				return;
			} else {
				// Handle other types of errors
				console.error("Unexpected error:", error);
				return;
			}
		}
	};

	const handleSetDisabled = (param: boolean) => {
		setDisabled(param);
	};

	const handleSendMessage = async (label: string, chat_id: string) => {
		const payload = {
			question: label,
			filter: {},
			conversation_id: chat_id,
			label: `use_case_${selectedUseCase}`,
		};

		if (selectedUseCase === 3) {
			payload.label = `use_case_${selectedUseCase}${
				selectedSubCase ? `.${selectedSubCase}` : ""
			}`;
		} else {
			payload.label = `use_case_${selectedUseCase}`;
		}

		if (selectedUseCase === 0 && messages.length === 0) {
			// Start new chat if a  user is sending message before selecting a use case.
			startChat(selectedUseCase);
		}

		if (!chat_id) {
			setShowCards(false);
			handleSelect(0);
			return;
		}

		// check user permissions and try sending message...
		try {
			handleSetDisabled(true);
			addMessage("", "left");

			// Close previous event source if it exists
			if (eventSource) {
				eventSource.cancel();
			}
			const token = localStorage.getItem("token");

			const response = await handleApiRequest(token, payload);

			if (response?.body) {
				const reader = response.body.getReader();
				setEventSource(reader);
			}
		} catch (error) {
			handleRequestError(error);
			// More specific error handling logic here
			if (error instanceof TypeError) {
				// Handle network errors (e.g., network down, CORS issues)
				console.error("Network error:", error);
				return;
			} else {
				// Handle other types of errors
				console.error("Unexpected error:", error);
				return;
			}
		}
	};

	useEffect(() => {
		if (selectedUseCase !== 0) {
			setMessages([]);
			setErrorCount(0);
			setShowMessage(false);
		}
		
		return () => {
			if (eventSource) {
				eventSource.cancel();
			}
		};
	}, [selectedUseCase, chatId]);

	useEffect(() => {
		if (!renderFromHistory) {
			if (chatId && selectedUseCase !== 3) {
				if (selectedUseCase == 0) {
					handleSendMessage(messages[0].text, chatId);
				} else {
					handleRadioChange("hi", chatId, "left");
				}
			}else if (chatId && selectedUseCase == 3 && selectedSubCase == 'b'){
				handleSendMessage("I know exactly what I want", chatId);
			}
		} else {
			setMessages([]);
			conversation.conversation?.map((conv: any, index: number) => {
				if (index == 0) {
					addMessage(conv.response, "left");
				} else {
					addMessage(conv.question, "right");
					addMessage(conv.response, "left");
				}
			});
			setRenderFromHistory(false);
		}
	}, [chatId]);

	useEffect(() => {
		if (eventSource) {
			const readStream = async () => {
				try {
					while (true) {
						const { done, value } = await eventSource.read();
						if (done) {
							break;
						}

						const buffer = new TextDecoder().decode(value, { stream: true });
						// Split buffer by newline characters
						const lines = buffer.split("\n");
						// Process complete lines
						lines.forEach((line) => {
							if (line.startsWith("data: ")) {
								const dataString = line.substring(6); // Remove 'data: ' prefix
								try {
									const data = JSON.parse(dataString);
									if (data.message) {
										addToLastMessage(data.message, "left");
									}
								} catch (e) {
									console.error("Error parsing JSON:", e);
									setErrorCount(errorCount + 1);
									return;
								}
							}
						});
					}
				} catch (e) {
					console.error("Error reading stream:", e);
					if (selectedUseCase == 3 && !selectedSubCase) {
						addToLastMessage(
							"Before proceeding furhter, please choose a path first.",
							"error"
						);
					} else {
						addToLastMessage(
							"An Error occured while processing your request. Kindly refresh the page and try again.",
							"error"
						);
					}
				} finally {
					setEventSource(null);
				}
			};
			readStream();
		}
	}, [eventSource]);

	return (
		<>
			<ChatHeader useCase={selectedUseCase} selectUseCase={handleSelect} />
			{showCards ? (
				<main className="d-flex flex-column justify-content-between align-items-center  min-vh-75">
					<div className="w-75 pt-5">
								<div className="justify-content-center pt-5">
									<div className="">
										<h1 className="mb-md-5 mb-4 text-center text-blue">
											How can I help you today?
										</h1>
										{/* <p className="text-center text-secondary">Choose an area of interest to get started.</p> */}
										<UseCaseCards selectUseCase={handleSelect} />
									</div>
								</div>
					</div>
					<div className="w-75">
						<div className="justify-content-center">
							<div className="">
								<p className="text-secondary">Trending questions this month:</p>
							
							<TopicSuggestionCards selectUseCase={handleSelect} setSelectSubCase={setSelectedSubCase} chatId={chatId} handleOptionSelect={handleRadioChange} startChat={startChat} />
						</div>
						</div>			
					</div>
				</main>
			) : (
				<Messages
					useCase={selectedUseCase}
					handleSubCase={setSelectedSubCase}
					selectedSubCase={selectedSubCase}
					messages={messages}
					chat_id={chatId}
					handleOptionSelect={handleRadioChange}
					showMessage={showMessage}
					handleUseCaseSelect={handleSelect}
					setDisabled={(param: boolean) => setDisabled(param)}
				/>
			)}
			<ChatFooter
				addMessage={addMessage}
				chat_id={chatId}
				getResponse={handleSendMessage}
				disabled={disabled}
				showCards={showCards}
				showNistCards={false}
				initialQuestion={initialQuestion}
			/>
			<ChatHistory
				onNavigate={(path: string) => {
					navigate(path);
				}}
			/>
		</>
	);
};

export default ChatBody;
